import * as Sentry from '@sentry/react';
import { FieldErrors, FieldValues } from 'react-hook-form';

/**
 * Reports a react-hook-form validation failure to Sentry as an expected
 * warning-level event. Use it inside `handleSubmit(onValid, onInvalid)`
 * when the caller wants validation failures to show up in the Sentry
 * dashboard without blowing up the issues inbox.
 *
 * Events are grouped by `screen` + the sorted list of failing fields, so
 * repeated failures with the same shape dedupe into a single issue.
 *
 * Example:
 *
 * ```tsx
 * const onInvalid = (errors: FieldErrors<FormValues>) =>
 *   reportFormValidationError('multi-access-account', errors);
 *
 * <form onSubmit={handleSubmit(onSubmit, onInvalid)}>...</form>
 * ```
 */
export function reportFormValidationError<T extends FieldValues>(
  screen: string,
  errors: FieldErrors<T>
): void {
  const fields = Object.keys(errors);
  if (fields.length === 0) return;

  const messages = Object.fromEntries(
    fields.map((key) => {
      const entry = (errors as Record<string, { message?: string }>)[key];
      return [key, entry?.message ?? null];
    })
  );

  Sentry.captureMessage(`[expected] form-validation:${screen}`, {
    level: 'warning',
    tags: {
      errorType: 'ibk',
      'error.domain': 'validation',
      screen,
    },
    extra: {
      fields,
      messages,
      source: 'form-validation',
    },
    fingerprint: ['ibk-form-validation', screen, ...[...fields].sort()],
  });

  Sentry.addBreadcrumb({
    category: 'form.validation',
    level: 'warning',
    message: `Form validation failed: ${screen}`,
    data: { fields, messages },
  });
}
